import {
  Badge, Button, DataTable, EmptyState, PageHeader, Row, useApi, useClampedPage, useI18n,
  type Column, type ConsentGapResponse,
} from '@daghfal/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Children with a consent nobody has answered (Q45, option 1).
 *
 * Nothing is assumed for a child registered before individual-photos consent existed, so until it is recorded
 * they are not photographed. Each name opens the child's screen, where the consents card records what the
 * signed form says. A refusal is an answer and takes that consent off the list.
 */
export function ConsentGaps() {
  const api = useApi();
  const { t } = useI18n();
  const [page, setPage] = useState(1);

  const gaps = useQuery({
    queryKey: ['consent-gaps', { page }],
    queryFn: () => api.children.consentGaps({ page, pageSize: 50 }),
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, gaps.data?.totalPages);

  const columns: Column<ConsentGapResponse>[] = [
    {
      key: 'name',
      header: t('children.column.name'),
      render: (child) => (
        <Link className="dg-row-button" to={`/children/${child.id}`}>
          {`${child.preferredName ?? child.firstName} ${child.lastName}`}
        </Link>
      ),
    },
    {
      key: 'class',
      header: t('children.column.class'),
      render: (child) => child.className ?? <span className="dg-card__hint">{t('children.unassigned')}</span>,
    },
    {
      key: 'photographable',
      header: t('consent.IndividualPhotos'),
      render: (child) => child.canBePhotographed
        ? <Badge tone="success">{t('consentGaps.photographable')}</Badge>
        : <Badge tone="danger">{t('consentGaps.notPhotographable')}</Badge>,
    },
    {
      key: 'unanswered',
      header: t('consentGaps.column.unanswered'),
      render: (child) => (
        <Row>
          {child.unanswered.map((type) => <Badge key={type} tone="warning">{t(`consent.${type}`)}</Badge>)}
        </Row>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('consentGaps.title')}
        lede={t('consentGaps.lede')}
        actions={<Link className="dg-button dg-button--ghost" to="/children">{t('action.back')}</Link>}
      />

      <DataTable
        caption={t('consentGaps.title')}
        columns={columns}
        rows={gaps.data?.items}
        rowKey={(child) => child.id}
        isLoading={gaps.isPending}
        error={gaps.error}
        onRetry={() => void gaps.refetch()}
        empty={<EmptyState icon="✅" title={t('consentGaps.empty')} />}
      />

      {gaps.data && gaps.data.totalPages > 1 && (
        <Row between>
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t('action.previous')}
          </Button>
          <span>{page} / {gaps.data.totalPages}</span>
          <Button variant="secondary" disabled={page >= gaps.data.totalPages} onClick={() => setPage((p) => p + 1)}>
            {t('action.next')}
          </Button>
        </Row>
      )}
    </>
  );
}
